import { doc, getDoc, setDoc, updateDoc } from "@firebase/firestore";
import { applicationError } from "../../../../redux/actions/error";
import { db } from "../../connection";
import logger from "../../../../utils/logger";
import generateRegistrationCode from "../../../../helperFunctions/generateRegistrationCode";

export const fbGetGroupRegistrationCode = (groupId) => {
  return new Promise((resolve, reject) => {
    const docRef = doc(db, "group", groupId);
    getDoc(docRef)
      .then((querySnapshot) => {
        if (querySnapshot.exists()) {
          resolve((querySnapshot.data() || {}).registrationCode);
        }
      })
      .catch((err) => {
        let message = "There was an error fetching the registration code";
        applicationError({
          message,
          intensity: "low",
          err,
          errorCode: "firebase",
        });
        reject({ message });
      });
  });
};

export const fbGetGroupPaymentDetails = (groupId) => {
  return new Promise((resolve, reject) => {
    const docRef = doc(db, "group", groupId);
    getDoc(docRef)
      .then((querySnapshot) => {
        if (querySnapshot.exists()) {
          resolve((querySnapshot.data() || {}).paymentDetails || {});
        } else {
          resolve(null);
        }
      })
      .catch((err) => {
        let message = "There was an error fetching payment details";
        applicationError({
          message,
          intensity: "low",
          err,
          errorCode: "firebase",
        });
        reject({ message });
      });
  });
};

export const fbChangeGroupRegistrationCode = async (
  groupId,
  role,
  registrationCode = null,
  registrationLink = null,
) => {
  try {
    if (!groupId || !role) {
      throw new Error("groupId and role are required");
    }

    if (!registrationCode) {
      registrationCode = generateRegistrationCode();
      registrationLink = createRegistrationLink(
        registrationCode,
        groupId,
        role,
      );
    } else if (!registrationLink) {
      registrationLink = createRegistrationLink(
        registrationCode,
        groupId,
        role,
      );
    }

    const docRef = doc(db, "group", groupId);
    await updateDoc(docRef, {
      registrationCode,
      registrationLink,
      regCodeRole: role,
    });

    return {
      success: true,
      data: {
        role,
        link: registrationLink,
        code: registrationCode,
      },
    };
  } catch (err) {
    logger.error("Failed to update registration code:", err);
    return {
      success: false,
      message: "There was an error updating the registration code",
    };
  }
};

export const fbChangeGroupPaymentDetails = (groupId, paymentDetails) => {
  return new Promise((resolve, reject) => {
    const docRef = doc(db, "group", groupId);
    setDoc(docRef, { paymentDetails }, { merge: true })
      .then(() => {
        resolve();
      })
      .catch((err) => {
        let message = "There was an error updating payment details";
        applicationError({
          message,
          intensity: "high",
          err,
          errorCode: "firebase",
        });
        reject({ message });
      });
  });
};

export const fbGetGroupSettings = (groupId) => {
  return new Promise((resolve, reject) => {
    const docRef = doc(db, "group", groupId);
    getDoc(docRef)
      .then((querySnapshot) => {
        if (querySnapshot.exists()) {
          resolve((querySnapshot.data() || {}).configuration);
        }
      })
      .catch((err) => {
        let message = "There was an error fetching group configurations";
        applicationError({
          message,
          intensity: "low",
          err,
          errorCode: "firebase",
        });
        reject({ message });
      });
  });
};

export function createRegistrationLink(regCode, groupId, role) {
  try {
    if (!regCode || !groupId || !role) {
      throw new Error(
        "Missing required parameters: regCode, groupId, and role are required",
      );
    }

    const encryptedParams = btoa(`register|${groupId}|${regCode}|${role}`);
    const regLink = `${import.meta.env.VITE_CHANT_LINKS_URL}?data=${encryptedParams}`;

    return regLink;
  } catch (err) {
    let message = "There was an error creating the registration link";
    throw { message };
  }
}

// Converts a legacy registration link (old btoa(encodeURIComponent(key=val&...)) format)
// to the compact positional format on-the-fly. DB value is never modified.
// Returns the original URL unchanged if it is already new format or if anything goes wrong.
export function normalizeRegistrationLink(storedUrl) {
  try {
    const url = new URL(storedUrl);
    const data = url.searchParams.get("data");
    if (!data) return storedUrl;

    const decoded = atob(data);

    // New format — already has register| prefix
    if (decoded.startsWith("register|")) return storedUrl;

    // Intermediate pipe format (groupId|regCode|role) — upgrade to new format
    if (decoded.includes("|")) {
      const [groupId, regCode, role] = decoded.split("|");
      if (groupId && regCode && role)
        return createRegistrationLink(regCode, groupId, role);
      return storedUrl;
    }

    // Old format
    const plain = decodeURIComponent(decoded).replace(/\$/g, "&");
    const params = new URLSearchParams(plain);
    const groupId = params.get("groupId");
    const regCode = params.get("regCode");
    const role = params.get("role");

    if (!groupId || !regCode || !role) return storedUrl;

    return createRegistrationLink(regCode, groupId, role);
  } catch {
    return storedUrl;
  }
}
